/**
 * Client-side extraction of an uploaded policy document down to what the
 * formatter accepts: plain text, or (for PDFs only) the raw bytes base64'd.
 *
 * Everything happens in the browser — a docx/xlsx never reaches the platform in
 * its original form. `mammoth` and `xlsx` are dynamically imported so the two
 * heavy parsers stay out of the main bundle and only load for an admin who
 * actually uploads that format.
 */

/** Extensions the file picker offers, and the only ones `sourceKindFor` accepts. */
export const ACCEPTED_EXTENSIONS = [
  '.md',
  '.markdown',
  '.txt',
  '.csv',
  '.docx',
  '.xlsx',
  '.pdf',
] as const;

export const FILE_ACCEPT_ATTR = ACCEPTED_EXTENSIONS.join(',');

/** How a file has to be read. Drives both the reader and the formatter field. */
export type SourceKind = 'text' | 'docx' | 'xlsx' | 'pdf';

const KIND_BY_EXTENSION: Record<string, SourceKind> = {
  '.md': 'text',
  '.markdown': 'text',
  '.txt': 'text',
  '.csv': 'text',
  '.docx': 'docx',
  '.xlsx': 'xlsx',
  '.pdf': 'pdf',
};

/** The file's extension, lowercased and including the dot. '' when it has none. */
export const extensionOf = (filename: string): string => {
  const dot = filename.lastIndexOf('.');
  return dot === -1 ? '' : filename.slice(dot).toLowerCase();
};

/** The filename minus its extension — the default document name for an upload. */
export const baseNameOf = (filename: string): string => {
  const ext = extensionOf(filename);
  return ext ? filename.slice(0, -ext.length) : filename;
};

/** null for an unsupported extension — the caller reports it and stops. */
export const sourceKindFor = (filename: string): SourceKind | null =>
  KIND_BY_EXTENSION[extensionOf(filename)] ?? null;

/**
 * Which formatter body field the extracted content belongs in. Only PDFs go as
 * bytes; every other kind is reduced to text in the browser first.
 */
export const formatterFieldFor = (kind: SourceKind): 'text' | 'pdfBase64' =>
  kind === 'pdf' ? 'pdfBase64' : 'text';

const readAsText = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(new Error(`Could not read ${file.name}.`));
    reader.readAsText(file);
  });

const readAsArrayBuffer = (file: File): Promise<ArrayBuffer> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (result instanceof ArrayBuffer) resolve(result);
      else reject(new Error(`Could not read ${file.name}.`));
    };
    reader.onerror = () => reject(new Error(`Could not read ${file.name}.`));
    reader.readAsArrayBuffer(file);
  });

/** Chunked so a multi-MB PDF can't blow the argument limit on String.fromCharCode. */
export const arrayBufferToBase64 = (buffer: ArrayBuffer): string => {
  const bytes = new Uint8Array(buffer);
  const CHUNK = 0x8000;
  let binary = '';
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  return btoa(binary);
};

const extractDocx = async (buffer: ArrayBuffer): Promise<string> => {
  const mammoth = (await import('mammoth')).default;
  const result = await mammoth.extractRawText({ arrayBuffer: buffer });
  return result.value;
};

/** Each sheet as CSV under a markdown heading, so sheet names survive the flattening. */
const extractXlsx = async (buffer: ArrayBuffer): Promise<string> => {
  const { read, utils } = await import('xlsx');
  const workbook = read(buffer, { type: 'array' });
  return workbook.SheetNames.map((name) => {
    const sheet = workbook.Sheets[name];
    return `# ${name}\n${sheet ? utils.sheet_to_csv(sheet) : ''}`;
  }).join('\n\n');
};

export interface ExtractedUpload {
  kind: SourceKind;
  /** Extracted plain text; null for a PDF, whose bytes go to the formatter instead. */
  text: string | null;
  /** Base64 PDF bytes; null for every other kind. */
  pdfBase64: string | null;
}

/**
 * A PDF's text can't be pulled out reliably in the browser, so the formatter
 * reads the bytes itself — which is also why we have no source text to store
 * for one (see `sourceTextFor`).
 */
export const extractUpload = async (file: File): Promise<ExtractedUpload> => {
  const kind = sourceKindFor(file.name);
  if (!kind) {
    throw new Error(
      `${file.name} isn't a supported file type. Upload ${ACCEPTED_EXTENSIONS.join(', ')}.`,
    );
  }

  if (kind === 'pdf') {
    return { kind, text: null, pdfBase64: arrayBufferToBase64(await readAsArrayBuffer(file)) };
  }
  if (kind === 'docx') {
    return { kind, text: await extractDocx(await readAsArrayBuffer(file)), pdfBase64: null };
  }
  if (kind === 'xlsx') {
    return { kind, text: await extractXlsx(await readAsArrayBuffer(file)), pdfBase64: null };
  }
  return { kind, text: await readAsText(file), pdfBase64: null };
};

/** The formatter request body for an extraction — exactly one content field. */
export const formatRequestBodyFor = (
  filename: string,
  extracted: ExtractedUpload,
): { filename: string; text?: string; pdfBase64?: string } =>
  formatterFieldFor(extracted.kind) === 'pdfBase64'
    ? { filename, pdfBase64: extracted.pdfBase64 ?? '' }
    : { filename, text: extracted.text ?? '' };

/**
 * What to keep as the policy's `sourceText`. A PDF has no client-side text, so
 * it gets a marker rather than an empty string — an admin reading the record
 * should see why the original isn't there.
 */
export const sourceTextFor = (extracted: ExtractedUpload): string =>
  extracted.kind === 'pdf' ? '(PDF source)' : (extracted.text ?? '');
